import hyperdom from 'hyperdom'
import ReturnValidationRules from './returnValidationRules.js'
import autocomplete from '../autocomplete.jsx'
import datePicker from '../datePicker.js'
import timePicker from '../timePicker.jsx'
var KEY_ENTER = 13
import datesNotes from '../flights/datesNotes.jsx'
import tripNotes from '../tripNotes.jsx'
import * as gtmDataLayer from '../analytics/gtm/dataLayer.js'
import FlightPreferences from './flightPreferences.jsx'
import {calculateLegs} from './tropicsLeg.js'

export default class ReturnCriteria {
  constructor (model) {
    this.model = model
    this.validator = new ReturnValidationRules(model, model.exploreFlights)
    this.enableFilterByDirectFlight = model.config.features.enableFilterByDirectFlight
    this.flightPreferences = new FlightPreferences(model)
  }

  onupdate () {
    gtmDataLayer.activateOptimize()
  }

  search () {
    const filterByDirectFlights = this.model.filterByDirectFlights

    this.model.dmpg.sendFlightSearchCustomEvent()
    var model = this.model
    var exploreFlights = model.exploreFlights
    var flights = model.flights
    flights.clearWarnings()

    var mode = model.config.flights.mode
    if (mode === 'enabled' && !flights.allowedActions.IsSearchAllowed) {
      mode = 'callback'
    }

    var operations = {
      enabled: () => {
        var origin = exploreFlights.departureAirport.slice(-3)
        var tripStartAirport = flights.defaultAirports.start
        var tripEndAirport = flights.defaultAirports.end
        model.showLoading()
        const criteria = {
          cabin: exploreFlights.cabin === 'any' ? undefined : exploreFlights.cabin,
          airlines: exploreFlights.airline === 'any' ? [] : [model.flights.criteria.model.exploreFlights.airline],
          segments: [
            {
              arrive: tripStartAirport,
              depart: origin,
              departDate: exploreFlights.getDepartDate(),
            },
            {
              arrive: origin,
              depart: tripEndAirport,
              departDate: exploreFlights.getReturnDate(),
            },
          ],
        }
        return model.api.searchFlights({
          criteria,
          departureCode: model.departure.code,
          tourCode: model.departure.tourCode,
          sellingCompanyCode: model.departure.sellingCompanyCode,
          party: model.party(),
          filterByDirectFlights,
        }).then(({results}) => {
          flights.searchResults = calculateLegs(results)
          if (flights.searchResults.length === 0) {
            flights.searchMode = 'noResults'
          } else {
            flights.searchMode = 'results'
          }
          model.hideLoading()
          this.refresh()
        }).catch((e) => {
          console.error(e)
          model.hideLoading()
          flights.searchMode = 'notAvailable'
          this.refresh()
        })
      },
      callback: () => {
        model.showLoading()
        setTimeout(() => { // just making this look like it is doing something
          flights.searchMode = 'notAvailable'
          model.hideLoading()
          this.refresh()
        }, 300)
      },
    }

    if (this.validate()) {
      operations[mode]()
    }
  }

  validate () {
    return ![this.validator.validate()].some(x => !x)
  }

  searchIfEnter (e){
    if (e.keyCode === KEY_ENTER) {
      e.preventDefault()

      this.search()
    }
  }

  renderErrors (id) {
    var flights = this.model.exploreFlights
    var error = (flights.validationErrors || {})[id]
    if (error) {
      return <div class="o-form-item__warning">
        { error }
      </div>
    }
  }

  validClass (id){
    var flights = this.model.exploreFlights
    var error = (flights.validationErrors || {})[id]
    return error ? 'invalid' : 'valid'
  }

  render () {
    var model = this.model
    var flights = model.flights
    var exploreFlights = model.exploreFlights
    var validator = this.validator
    var dateFormat = model.config.dateFormat
    const {placeholderCity} = model.config.flights
    const showTripNotes = model.config.flights.requireTripNotesRead && this.model.departure.notes && this.model.departure.notes.length
    if (!model.departure.notes || model.departure.notes.length === 0) {
      model.exploreFlights.tripNotesRead = true
    }
    return <div className='flights__search-criteria'>
      <div className='flights__airports-container'>
        {this.flightPreferences}
        {datesNotes(model)}
        <div class="flights-search__form-header">Flights</div>
        <div class ="flights-search__form">
          <div className="o-form-item flights__airports-departure">
            <label htmlFor="departureAirport" className="o-form-item__label flights__label" key="departure-airport">
                Flying from
            </label>
            <i className="o-form-item__icon -new-ui_pin"></i>
            {autocomplete({
              id: "departureAirport",
              placeholder: placeholderCity ? `Try ${placeholderCity}` : 'City name or airport name',
              binding: [exploreFlights, 'departureAirport'],
              suggestions: model.homeCountryAirports,
              validate: validator.rules.departureAirport,
              onkeydown: this.searchIfEnter.bind(this),
              disableDebounce: this.model.disableLoadingTimeout,
            })}
            {this.renderErrors('departureAirport')}
          </div>
          <div className='flights__airports-icon-between'></div>
          <div className="o-form-item flights__airports-arrive">
            <label htmlFor="arriveAirport" className="o-form-item__label flights__label" key="arrive-airport">
                Flying to*
            </label>
            <i className="o-form-item__icon -new-ui_pin"></i>
            <input className={'o-form-item__txt-input ' + this.validClass('departureDate')}
              type="text"
              id="arriveAirport"
              readOnly
              placeholder={model.arriveAirportName}/>
            <div className="flights__airports-arrive-info">* Flying to is defined by the trip start/end locations</div>
            {this.renderErrors('arriveAirport')}
          </div>
        </div>
      </div>
      <div className='flights__dates-container'>
        <div className="o-form-item flights__dates-departure">
          <label htmlFor="departureDate" className="o-form-item__label flights__label" key="departure-date">
              Flight dates
          </label>
          <i className="o-form-item__icon -new-ui_calendar"></i>
          {datePicker(
            <input className={'o-form-item__txt-input ' + this.validClass('departureDate')}
              type="text"
              id="departureDate"
              readOnly
              onblur={validator.rules.departureDate}
              onkeydown={this.searchIfEnter.bind(this)}
              placeholder='Departure date'
            />,
            {
              binding: [exploreFlights, 'departureDate'],
              format: dateFormat,
              parseFormat: dateFormat,
              defaultDate: flights.defaultDepartDate,
            }
          )}
          <div className="o-form-item u-inbl u-margin-left-small form-item-departure-time">
            {timePicker({binding: [exploreFlights, 'departTime']})}
          </div>
          {this.renderErrors('departureDate')}
        </div>
        <div className="o-form-item flights__dates-arrive">
          <label htmlFor="return" className="o-form-item__label flights__label" key="return-date">
              &nbsp;
          </label>
          <i className="o-form-item__icon -new-ui_calendar"></i>
          {datePicker(
            <input className={'o-form-item__txt-input ' + this.validClass('returnDate')}
              type="text"
              id="return"
              readOnly
              onblur={validator.rules.returnDate}
              onkeydown={this.searchIfEnter.bind(this)}
              placeholder='Return date'/>,
            {
              binding: [exploreFlights, 'returnDate'],
              format: dateFormat,
              parseFormat: dateFormat,
              defaultDate: flights.defaultReturnDate,
            }
          )}
          <div className="o-form-item u-inbl u-margin-left-small form-item-return-time">
            {timePicker({binding: [exploreFlights, 'returnTime']})}
          </div>
          {this.renderErrors('returnDate')}
        </div>
      </div>
      <div class="u-margin-top-medium">
        { showTripNotes
          ? <div class="flights__trip-notes" onmouseleave={() => flights.showTripNotes = false}>
            <label class="o-choice-item -fullWidth">
              <input onclick={validator.rules.tripNotesRead} binding={[exploreFlights, 'tripNotesRead']} type="checkbox" class="o-choice-item__checkbox" name="termsAndConditions" id="termsAndConditions"/>
              <div class="o-choice-item__content">
                I have read the <a href="#" onclick={(ev) => {ev.preventDefault();flights.showTripNotes = !flights.showTripNotes}} class="trip-notes-open">trip notes</a>
              </div>
            </label>
            {this.renderErrors('tripNotesRead')}
            <div class={['help-content', {'trip-notes' : flights.showTripNotes}]}>
              { tripNotes(this.model.departure.notes) }
            </div>
          </div>
          : undefined
        }
        <div className='flights__button'>
          { this.enableFilterByDirectFlight
            ? <div class="flights__criteria-filterByDirectFlights">
              <label className='o-choice-item'>
                <input type="checkbox" binding="model.filterByDirectFlights" className='filterByDirectFlights o-choice-item__checkbox o-choice-item__checkbox-small' />
                <div className='o-choice-item__content o-choice-item__content_small'>
                  <span><strong>Only show direct flights</strong></span><span>&nbsp;</span>
                </div>
              </label>
            </div>
            : undefined
          }
          <button type="button" onclick={this.search.bind(this)} className="btn-secondary">Find Flights</button>
        </div>
      </div>
    </div>
  }
}
